import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Phone, Calendar, Star, X, RotateCcw } from 'lucide-react';

/**
 * GoogleWalkthrough (Jose 2026-10-08) — the Way-1 "show me how" for booking via Google. We can't draw a
 * live ring on Google's own page, so this is the HYBRID Jose landed on: the simplicity of an illustrated
 * 3-step (Search → tap Book → you're on our page) WITH a touch of animation (the Book button pulses, a
 * finger taps, scenes reveal in sequence). Phone-savvy clients glance and go "got it"; hesitant ones
 * get shown. It funnels into the Way-2 live tour (/book?tour=1) at the end — once you tap Book you're
 * on our surface and the blinking walkthrough takes over. Illustrative only: NO live "open Google" link
 * (Google's Book button still points to Fresha until the GBP repoint) — add that after the cutover.
 */

const SCENES = [
  { n: 1, caption: 'Search “SLIK Barber Lounge”', dir: '👆' },
  { n: 2, caption: 'Tap the Book button', dir: '👆' },
  { n: 3, caption: 'You’re on our page — we guide you from here', dir: '👇' },
];
const TOTAL = SCENES.length;

export const GoogleWalkthrough: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [scene, setScene] = useState(0);

  // Restart from the top each time it opens.
  useEffect(() => { if (open) setScene(0); }, [open]);

  // Auto-advance, then hold on the final scene (replay button resets it).
  useEffect(() => {
    if (!open || scene >= TOTAL - 1) return;
    const t = setTimeout(() => setScene((s) => Math.min(TOTAL - 1, s + 1)), 2200);
    return () => clearTimeout(t);
  }, [open, scene]);

  if (!open) return null;
  const s = SCENES[scene];

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <style>{`
        @keyframes gwFade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes gwCaret{0%,100%{opacity:1}50%{opacity:0}}
        @keyframes gwPulse{0%,100%{box-shadow:0 0 0 0 rgba(156,95,40,.6)}50%{box-shadow:0 0 0 9px rgba(156,95,40,0)}}
        @keyframes gwTap{0%,100%{transform:translateY(0)}50%{transform:translateY(5px)}}
        @keyframes gwArrow{0%,100%{transform:translateY(0)}50%{transform:translateY(5px)}}
      `}</style>

      <div className="w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="relative rounded-3xl bg-white shadow-2xl overflow-hidden">
          {/* close */}
          <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center">
            <X className="w-4 h-4 text-gray-600" />
          </button>

          <div className="px-5 pt-5 pb-3 text-center">
            <h3 className="text-lg font-extrabold text-ink">Book from Google</h3>
            <p className="text-xs font-semibold text-gray-500 mt-0.5">Here’s all it takes 👇</p>
          </div>

          {/* PHONE */}
          <div className="px-6 pb-2">
            <div className="mx-auto w-[220px] rounded-[28px] border-[6px] border-ink/90 bg-gray-50 overflow-hidden" style={{ height: 300 }}>
              {/* notch */}
              <div className="h-5 bg-ink/90 flex items-center justify-center">
                <div className="w-14 h-1.5 rounded-full bg-white/30" />
              </div>

              <div key={scene} className="p-3 h-[274px]" style={{ animation: 'gwFade .4s ease-out' }}>
                {/* SCENE 1 — SEARCH */}
                {scene === 0 && (
                  <div className="h-full flex flex-col items-center justify-center gap-5">
                    <div className="text-2xl font-bold tracking-tight">
                      <span className="text-blue-500">G</span><span className="text-red-500">o</span>
                      <span className="text-yellow-500">o</span><span className="text-blue-500">g</span>
                      <span className="text-green-600">l</span><span className="text-red-500">e</span>
                    </div>
                    <div className="w-full flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-2 shadow-sm">
                      <Search className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="text-sm font-semibold text-gray-800 leading-none">
                        SLIK Barber Lounge
                        <span className="inline-block w-0.5 h-4 bg-slik-purple align-middle ml-0.5" style={{ animation: 'gwCaret 1s step-end infinite' }} />
                      </span>
                    </div>
                  </div>
                )}

                {/* SCENE 2 — RESULT + BOOK */}
                {scene === 1 && (
                  <div className="h-full flex flex-col justify-center">
                    <div className="rounded-xl bg-white border border-gray-200 shadow-sm p-3">
                      <div className="flex items-start gap-2.5">
                        <div className="w-10 h-10 rounded-lg bg-ink flex items-center justify-center text-lg shrink-0">💈</div>
                        <div className="min-w-0">
                          <div className="text-sm font-extrabold text-ink leading-tight">SLIK Barber Lounge</div>
                          <div className="flex items-center gap-1 text-[11px] font-bold text-amber-600">
                            4.9 <Star className="w-3 h-3 fill-amber-500 text-amber-500" /><Star className="w-3 h-3 fill-amber-500 text-amber-500" /><Star className="w-3 h-3 fill-amber-500 text-amber-500" /><Star className="w-3 h-3 fill-amber-500 text-amber-500" /><Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                          </div>
                          <div className="text-[10px] text-gray-500">Barber shop · Florham Park</div>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-1.5">
                        <div className="flex flex-col items-center gap-0.5 rounded-lg border border-gray-200 py-1.5 text-[9px] font-bold text-gray-500"><MapPin className="w-3.5 h-3.5" />Directions</div>
                        <div className="flex flex-col items-center gap-0.5 rounded-lg border border-gray-200 py-1.5 text-[9px] font-bold text-gray-500"><Phone className="w-3.5 h-3.5" />Call</div>
                        <div className="relative flex flex-col items-center gap-0.5 rounded-lg py-1.5 text-[9px] font-extrabold text-white bg-blue-600" style={{ animation: 'gwPulse 1.2s ease-in-out infinite' }}>
                          <Calendar className="w-3.5 h-3.5" />Book
                          <span className="absolute -bottom-5 -right-2 text-xl" style={{ animation: 'gwTap 1s ease-in-out infinite' }}>👆</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* SCENE 3 — ON OUR PAGE */}
                {scene === 2 && (
                  <div className="h-full flex flex-col justify-center gap-3">
                    <div className="text-center text-base font-extrabold text-slik-purple tracking-tight">slik</div>
                    <div className="rounded-xl bg-white border border-gray-200 shadow-sm p-3">
                      <div className="text-[11px] font-extrabold text-ink mb-2">Pick your barber</div>
                      <div className="flex gap-2 mb-3">
                        <div className="flex flex-col items-center gap-1"><div className="w-9 h-9 rounded-full bg-slik-purple/20 ring-2 ring-slik-purple" /><span className="text-[8px] font-bold text-gray-600">You pick</span></div>
                        <div className="flex flex-col items-center gap-1"><div className="w-9 h-9 rounded-full bg-gray-200" /><span className="text-[8px] text-gray-400">·</span></div>
                        <div className="flex flex-col items-center gap-1"><div className="w-9 h-9 rounded-full bg-gray-200" /><span className="text-[8px] text-gray-400">·</span></div>
                      </div>
                      <div className="flex gap-1.5">
                        <div className="flex-1 text-center text-[9px] font-bold text-gray-500 rounded-md border border-gray-200 py-1">10:00</div>
                        <div className="flex-1 text-center text-[9px] font-extrabold text-white rounded-md bg-slik-purple py-1">10:30</div>
                        <div className="flex-1 text-center text-[9px] font-bold text-gray-500 rounded-md border border-gray-200 py-1">11:00</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* dots */}
          <div className="flex justify-center gap-1.5 py-2">
            {SCENES.map((_, i) => (
              <div key={i} className={`h-1.5 rounded-full transition-all ${i === scene ? 'w-5 bg-slik-purple' : 'w-1.5 bg-gray-300'}`} />
            ))}
          </div>

          {/* COACH BAR — same language as the live booking tour */}
          <div className="mx-4 mb-3 rounded-2xl bg-slik-navy text-white px-4 py-3 flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slik-purple font-extrabold shrink-0">{s.n}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-white/60 font-bold">Step {s.n} of {TOTAL} · on Google</div>
              <div className="text-sm font-extrabold leading-tight">
                {s.caption} <span style={{ display: 'inline-block', animation: 'gwArrow 1s ease-in-out infinite' }}>{s.dir}</span>
              </div>
            </div>
          </div>

          {/* ACTIONS */}
          <div className="px-4 pb-4 flex items-center gap-2">
            {scene >= TOTAL - 1 ? (
              <button onClick={() => setScene(0)} className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-button border border-gray-300 text-sm font-bold text-gray-600 hover:bg-gray-50">
                <RotateCcw className="w-4 h-4" /> Replay
              </button>
            ) : (
              <button onClick={() => setScene(TOTAL - 1)} className="px-3 py-2.5 rounded-button border border-gray-300 text-sm font-bold text-gray-500 hover:bg-gray-50">Skip</button>
            )}
            <Link to="/book?tour=1" className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-button bg-slik-purple text-white font-extrabold shadow-button hover:opacity-90">
              <Calendar className="w-5 h-5" /> Book now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleWalkthrough;
